/**
 * CompetitorsView - NVIDIA Competitors page view
 *
 * Displays NVIDIA competitors with filtering and customization options
 */

import { BaseView } from './BaseView';
import { DataService } from '../services/DataService';
import type { Competitor, CompetitorSourceType } from '@/types';
import * as XLSX from 'xlsx';

export class CompetitorsView extends BaseView {
	private dataService: DataService;
	private allCompetitors: Competitor[] = [];
	private selectedSourceTypes: CompetitorSourceType[] = [];
	private currentPage: number = 1;
	private readonly pageSize: number = 25;
	private sortDirection: 'asc' | 'desc' = 'asc';

	constructor() {
		super();
		this.dataService = new DataService();
	}

	/**
	 * Render the competitors view content
	 */
	render(): string {
		return `
			<style>
				.competitors-table {
					width: 100%;
					border-collapse: collapse;
					font-family: Arial, sans-serif;
					font-size: 11px;
					background: #fff;
					margin-top: 10px;
				}
				.competitors-table th {
					background: #e6e8ea;
					border: 1px solid #999;
					padding: 6px 8px;
					font-weight: bold;
					color: #333;
					text-align: left;
					position: relative;
				}
				.competitors-table td {
					border: 1px solid #ccc;
					padding: 4px 8px;
					color: #333;
					vertical-align: top;
				}
				.competitors-table tr:nth-child(even) { background: #f8f9fa; }
				.competitors-table tr:hover { background: #e8f4fd; }
				.competitors-table .header-row th {
					background: #e6e8ea;
					border: 1px solid #999;
					padding: 8px 10px;
					font-size: 15px;
					font-weight: bold;
					color: #333;
					text-align: left;
					border-bottom: 2px solid #bbb;
				}
				.competitors-table .col-competitor { width: 38%; }
				.competitors-table .col-revenue { width: 18%; text-align: right; }
				.competitors-table .col-date { width: 14%; text-align: center; }
				.competitors-table .col-source { width: 30%; }
				.competitors-table .expand-icon-row { color: #555; cursor: pointer; }
				.competitors-table td.col-revenue,
				.competitors-table td.col-date {
					font-family: Arial, sans-serif;
					font-size: 12px;
					font-weight: normal;
					color: #000;
				}
				.blue-link {
					color: rgb(0, 86, 214);
					text-decoration: none;
					font-family: Arial, sans-serif;
					font-size: 12px;
					font-weight: 700;
					cursor: pointer;
				}
				.blue-link:hover { text-decoration: underline; }
				.competitors-table td.col-competitor { background: #e9f0f7; }
				.sort-arrow { float:right; font-size:10px; color:#666; }
				.filters-container {
					width: 98%;
					margin: 10px auto 14px auto;
					background: #f7f7f7;
					border: 1px solid #bfbfbf;
					padding: 0;
				}
				.filters-title {
					display: flex;
					align-items: center;
					gap: 6px;
					padding: 6px 10px;
					font-weight: bold;
					color: #333;
					background: linear-gradient(#f3f3f4, #d9dcdf);
					border-bottom: 1px solid #bfbfbf;
					box-shadow: inset 0 1px #ffffff;
					cursor: pointer;
				}
				.filters-body {
					background: #e6e6e6;
					padding: 12px 12px 12px 12px;
					border-top: 1px solid #cfcfcf;
				}
				.filters-title .collapse-icon {
					width: 14px;
					height: 14px;
					display: inline-flex;
					align-items: center;
					justify-content: center;
					border: 1px solid #9aa0a6;
					background: linear-gradient(#ffffff, #e4e7ea);
					font-size: 11px;
					line-height: 1;
					color: #333;
					box-shadow: inset 0 1px #ffffff;
				}
				.dual-list {
					display: flex;
					align-items: center;
					gap: 8px;
					margin-top: 6px;
				}
				.list-box {
					flex: 1 1 45%;
					background: #fff;
					border: 1px solid #bbb;
					padding: 10px;
				}
				.list-box .box-title {
					font-weight: bold;
					color: #333;
					margin-bottom: 4px;
				}
				.list-box select {
					width: 100%;
					height: 120px;
					border: 1px solid #ccc;
					font-size: 12px;
				}
				.arrow-buttons {
					display: flex;
					flex-direction: column;
					gap: 6px;
				}
				.arrow-buttons button {
					width: 34px;
					height: 26px;
					border: 1px solid #999;
					background: #eee;
					cursor: pointer;
				}
				.filters-actions { display:flex; justify-content:flex-end; margin-top:8px; }
				.go-button {
					padding: 4px 10px;
					border: 1px solid #9aa0a6;
					background: linear-gradient(#ffffff, #e4e7ea);
					border-radius: 3px;
					font-size: 12px;
					color: #333;
					cursor: pointer;
					box-shadow: inset 0 1px #ffffff;
				}
				.go-button:hover { background: #f0f3f6; }

				@media print {
					.header,
					.navbar,
					.sidebar,
					.button-row {
						display: none !important;
					}

					@page {
						size: letter;
						margin: 0.5in;
					}

					body {
						margin: 0;
						padding: 0;
					}

					.content-container {
						display: block;
					}

					.main-content {
						width: 100% !important;
						margin: 0 !important;
					}
				}
			</style>

			<div class="company-header">
				<div class="company-name">NVIDIA Corporation (NasdaqGS:NVDA) Competitors</div>
			</div>

			<div class="button-row">
				<div class="tool-button" id="export-excel-btn">
					<img src="../img/icon1.png" alt="Export" onerror="this.src='../img/icon-placeholder.png'">
					<span class="button-text">Export to Excel</span>
				</div>
				<div class="tool-button print-button">
					<i data-lucide="printer" style="width: 16px; height: 16px; margin-right: 3px;"></i>
					<span class="button-text">Print</span>
				</div>
			</div>

			<div class="filters-container" aria-label="Customize View">
				<div class="filters-title"><span class="collapse-icon" id="filtersToggleIcon">-</span> Customize View</div>
				<div class="filters-body" id="filtersBody">
					<div class="dual-list">
						<div class="list-box">
							<div class="box-title">Available Source Types</div>
							<select id="availableList" multiple>
								<option>Named by Company</option>
								<option>Named by Competitor</option>
								<option>Named by Third Party</option>
							</select>
						</div>
						<div class="arrow-buttons">
							<button type="button" id="moveRight" title="Add selected">&gt;</button>
							<button type="button" id="moveAllRight" title="Add all">&gt;&gt;</button>
							<button type="button" id="moveAllLeft" title="Remove all">&lt;&lt;</button>
							<button type="button" id="moveLeft" title="Remove selected">&lt;</button>
						</div>
						<div class="list-box">
							<div class="box-title">Selected Source Types</div>
							<select id="selectedList" multiple></select>
						</div>
					</div>
					<div class="filters-actions">
						<button type="button" id="filtersApply" class="go-button">Go</button>
					</div>
				</div>
			</div>

			<div style="width:98%; margin:auto; margin-top:12px;">
				<table class="competitors-table">
					<thead>
						<tr class="header-row">
							<th colspan="4">Competitors</th>
						</tr>
						<tr>
							<th class="col-competitor">Competitor's Name <span class="sort-arrow">▲</span></th>
							<th class="col-revenue">LTM Revenue ($mm)</th>
							<th class="col-date">LTM Date</th>
							<th class="col-source">Source</th>
						</tr>
					</thead>
					<tbody id="competitors-tbody"></tbody>
				</table>

				<div class="pagination" id="pagination" style="margin-top:6px;"></div>
			</div>
		`;
	}

	/**
	 * Initialize competitors table after view is mounted
	 */
	async onMount(): Promise<void> {
		try {
			// Load competitors from DataService
			this.allCompetitors = await this.dataService.loadCompetitors();

			// Set up dual-list filter handlers
			this.initializeFilters();

			// Set up Export to Excel button handler
			const exportBtn = this.container?.querySelector('#export-excel-btn');
			if (exportBtn) {
				this.addEventListener(exportBtn as HTMLElement, 'click', () => {
					this.exportToExcel();
				});
			}

			// Set up Print button handler
			const printBtn = this.container?.querySelector('.print-button');
			if (printBtn) {
				this.addEventListener(printBtn as HTMLElement, 'click', () => {
					window.print();
				});
			}

			// Set up sort handler for Competitor's Name column header
			const nameHeader = this.container?.querySelector('.col-competitor');
			if (nameHeader) {
				(nameHeader as HTMLElement).style.cursor = 'pointer';
				this.addEventListener(nameHeader as HTMLElement, 'click', () => {
					this.toggleSort();
				});
			}

			// Render initial data (no filters applied)
			this.renderCompetitorsTable();
		} catch (error) {
			console.error('[CompetitorsView] Failed to load competitors:', error);
		}
	}

	/**
	 * Initialize filter controls
	 */
	private initializeFilters(): void {
		const moveRight = this.container?.querySelector('#moveRight');
		const moveLeft = this.container?.querySelector('#moveLeft');
		const moveAllRight = this.container?.querySelector('#moveAllRight');
		const moveAllLeft = this.container?.querySelector('#moveAllLeft');
		const applyButton = this.container?.querySelector('#filtersApply');
		const titleBar = this.container?.querySelector('.filters-title');
		const filtersBody = this.container?.querySelector('#filtersBody');
		const toggleIcon = this.container?.querySelector('#filtersToggleIcon');

		if (moveRight) {
			this.addEventListener(moveRight as HTMLElement, 'click', () => {
				this.moveSelected('availableList', 'selectedList');
			});
		}

		if (moveLeft) {
			this.addEventListener(moveLeft as HTMLElement, 'click', () => {
				this.moveSelected('selectedList', 'availableList');
			});
		}

		if (moveAllRight) {
			this.addEventListener(moveAllRight as HTMLElement, 'click', () => {
				this.moveAll('availableList', 'selectedList');
			});
		}

		if (moveAllLeft) {
			this.addEventListener(moveAllLeft as HTMLElement, 'click', () => {
				this.moveAll('selectedList', 'availableList');
			});
		}

		if (applyButton) {
			this.addEventListener(applyButton as HTMLElement, 'click', () => {
				this.applyFilters();
			});
		}

		// Collapse/expand toggle
		if (titleBar && filtersBody && toggleIcon) {
			this.addEventListener(titleBar as HTMLElement, 'click', () => {
				const isHidden = (filtersBody as HTMLElement).style.display === 'none';
				(filtersBody as HTMLElement).style.display = isHidden ? 'block' : 'none';
				toggleIcon.textContent = isHidden ? '-' : '+';
			});
		}
	}

	/**
	 * Move selected options from one list to another
	 */
	private moveSelected(fromId: string, toId: string): void {
		const fromSelect = this.container?.querySelector(`#${fromId}`) as HTMLSelectElement | null;
		const toSelect = this.container?.querySelector(`#${toId}`) as HTMLSelectElement | null;

		if (!fromSelect || !toSelect) return;

		const selectedOptions = Array.from(fromSelect.options).filter(opt => opt.selected);

		selectedOptions.forEach(opt => {
			const newOption = document.createElement('option');
			newOption.textContent = opt.textContent;
			newOption.value = opt.value;
			toSelect.appendChild(newOption);
			fromSelect.removeChild(opt);
		});
	}

	/**
	 * Move all options from one list to another
	 */
	private moveAll(fromId: string, toId: string): void {
		const fromSelect = this.container?.querySelector(`#${fromId}`) as HTMLSelectElement | null;
		const toSelect = this.container?.querySelector(`#${toId}`) as HTMLSelectElement | null;

		if (!fromSelect || !toSelect) return;

		const allOptions = Array.from(fromSelect.options);

		allOptions.forEach(opt => {
			const newOption = document.createElement('option');
			newOption.textContent = opt.textContent;
			newOption.value = opt.value;
			toSelect.appendChild(newOption);
			fromSelect.removeChild(opt);
		});
	}

	/**
	 * Apply selected filters and re-render table
	 */
	private applyFilters(): void {
		const selectedList = this.container?.querySelector('#selectedList') as HTMLSelectElement | null;

		if (!selectedList) return;

		// Get selected source types
		this.selectedSourceTypes = Array.from(selectedList.options)
			.map(opt => opt.textContent?.trim() as CompetitorSourceType)
			.filter(Boolean);


		// Reset to page 1
		this.currentPage = 1;

		// Re-render table with filters
		this.renderCompetitorsTable();
	}

	/**
	 * Render competitors table
	 */
	private renderCompetitorsTable(): void {
		const tbody = this.container?.querySelector('#competitors-tbody');

		if (!tbody) {
			console.warn('[CompetitorsView] Competitors tbody not found');
			return;
		}

		tbody.innerHTML = '';

		// Filter competitors based on selected source types
		let filteredCompetitors = this.selectedSourceTypes.length === 0
			? this.allCompetitors
			: this.allCompetitors.filter(comp => this.selectedSourceTypes.includes(comp.sourceType));

		// Apply sorting
		filteredCompetitors = this.sortCompetitors([...filteredCompetitors]);

		// Calculate pagination
		const totalFiltered = filteredCompetitors.length;
		const totalPages = Math.max(1, Math.ceil(totalFiltered / this.pageSize));
		const pageIdx = Math.min(Math.max(this.currentPage - 1, 0), totalPages - 1);

		const startIdx = pageIdx * this.pageSize;
		const endIdx = Math.min(startIdx + this.pageSize, totalFiltered);
		const pageData = filteredCompetitors.slice(startIdx, endIdx);


		// Render rows
		pageData.forEach(row => {
			const tr = document.createElement('tr');

			const ltmDate = this.normalizeLtmDate(row.ltmDate);

			tr.innerHTML = `
				<td class="col-competitor">${row.competitor}</td>
				<td class="col-revenue">${row.revenueMm}</td>
				<td class="col-date">${ltmDate}</td>
				<td class="col-source">${row.source}</td>
			`;

			tbody.appendChild(tr);
		});

		// Update pagination
		this.updatePagination(filteredCompetitors.length, pageIdx);
	}

	/**
	 * Normalize LTM date to ensure it's not after March 2025
	 */
	private normalizeLtmDate(ltmDate: string): string {
		if (!ltmDate || ltmDate === '-') return ltmDate;

		// Parse date format: Jun-30-2025
		const parts = ltmDate.split('-');
		if (parts.length !== 3) return ltmDate;

		const monthMap: Record<string, number> = {
			'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
			'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
		};

		const monthStr = parts[0];
		const dayStr = parts[1];
		const yearStr = parts[2];

		if (!monthStr || !yearStr) return ltmDate;

		const monthNum = monthMap[monthStr];
		const yearNum = parseInt(yearStr, 10);

		if (!monthNum || !yearNum) return ltmDate;

		// Clamp to March 2025
		if (yearNum === 2025 && monthNum > 3) {
			return `Mar-${dayStr}-2025`;
		}

		return ltmDate;
	}

	/**
	 * Update pagination UI
	 */
	private updatePagination(totalItems: number, currentPageIdx: number): void {
		const pagination = this.container?.querySelector('#pagination');

		if (!pagination) return;

		const totalPages = Math.ceil(totalItems / this.pageSize);
		const startIdx = currentPageIdx * this.pageSize + 1;
		const endIdx = Math.min((currentPageIdx + 1) * this.pageSize, totalItems);

		if (totalItems <= this.pageSize) {
			pagination.innerHTML = `Viewing 1-${totalItems} of ${totalItems} Competitors`;
			return;
		}

		// Build page links
		const links: string[] = [];

		for (let p = 1; p <= totalPages; p++) {
			const blockStart = (p - 1) * this.pageSize + 1;
			const blockEnd = Math.min(p * this.pageSize, totalItems);

			if (p === currentPageIdx + 1) {
				links.push(`<span class="current">View ${blockStart}-${blockEnd}</span>`);
			} else {
				links.push(`<a href="#" data-page="${p}">${blockStart}-${blockEnd}</a>`);
			}
		}

		const nextLink = currentPageIdx + 1 < totalPages
			? `<a href="#" data-page="${currentPageIdx + 2}">>></a>`
			: `>>`;

		pagination.innerHTML = `Viewing ${startIdx}-${endIdx} of ${totalItems} Competitors [${links.join(' | ')} | ${nextLink}]`;

		// Attach click handlers
		const pageLinks = pagination.querySelectorAll('a[data-page]');
		pageLinks.forEach(link => {
			this.addEventListener(link as HTMLElement, 'click', (e: Event) => {
				e.preventDefault();
				const target = e.currentTarget as HTMLElement;
				const pageNum = parseInt(target.getAttribute('data-page') || '1', 10);
				this.currentPage = pageNum;
				this.renderCompetitorsTable();
			});
		});
	}

	/**
	 * Export competitors data to Excel
	 */
	private exportToExcel(): void {
		// Get filtered competitors (respect current filters)
		const filteredCompetitors = this.selectedSourceTypes.length === 0
			? this.allCompetitors
			: this.allCompetitors.filter(comp => this.selectedSourceTypes.includes(comp.sourceType));

		// Sort competitors if sort is active
		const sortedCompetitors = this.sortCompetitors([...filteredCompetitors]);

		// Prepare data for Excel export
		const excelData = sortedCompetitors.map(comp => ({
			"Competitor's Name": comp.competitor,
			"LTM Revenue ($mm)": comp.revenueMm,
			"LTM Date": this.normalizeLtmDate(comp.ltmDate),
			"Source": comp.source
		}));

		// Create worksheet
		const worksheet = XLSX.utils.json_to_sheet(excelData);

		// Create workbook
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, 'Competitors');

		// Generate filename with current date
		const dateStr = new Date().toISOString().split('T')[0];
		const filename = `NVIDIA-Competitors-${dateStr}.xlsx`;

		// Download file
		XLSX.writeFile(workbook, filename);

	}

	/**
	 * Toggle sort direction and re-render
	 */
	private toggleSort(): void {
		this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';

		// Update sort arrow in header
		const sortArrow = this.container?.querySelector('.sort-arrow');
		if (sortArrow) {
			sortArrow.textContent = this.sortDirection === 'asc' ? '▲' : '▼';
		}

		// Reset to page 1 and re-render
		this.currentPage = 1;
		this.renderCompetitorsTable();
	}

	/**
	 * Sort competitors array by name
	 */
	private sortCompetitors(competitors: Competitor[]): Competitor[] {
		return competitors.sort((a, b) => {
			const nameA = a.competitor.toLowerCase();
			const nameB = b.competitor.toLowerCase();

			if (this.sortDirection === 'asc') {
				return nameA.localeCompare(nameB);
			} else {
				return nameB.localeCompare(nameA);
			}
		});
	}
}
